import { Vec3 } from '@/core/math/Vec3';
import { fbm3 } from '@/core/math/noise';
import { clamp, smoothstep } from '@/core/math/scalar';
import { R_GAS } from '@/core/units';
import { CelestialBody } from '@/universe/CelestialBody';

export interface AtmosphereSample {
  /** kg/m^3 */
  density: number;
  /** Pa */
  pressure: number;
  /** K */
  temperature: number;
  /** m/s */
  speedOfSound: number;
  /** Wind velocity in the body-fixed frame, m/s. */
  wind: Vec3;
}

const GAMMA = 1.4;

/**
 * Exponential atmosphere model.
 *
 *     density(h) = surfaceDensity * exp(-h / scaleHeight)
 *
 * with a smooth taper to exactly zero across the last 15% of the modelled
 * column. The taper matters: an abrupt cutoff produces a discontinuity in drag
 * that shows up as a visible jolt during ascent and as an unstable orbit
 * boundary, and it would make time-warp safety checks flicker.
 */
export class AtmosphereModel {
  constructor(private readonly body: CelestialBody) {}

  get top(): number {
    return this.body.atmosphereTop;
  }

  get exists(): boolean {
    return this.body.hasAtmosphere;
  }

  densityAt(altitude: number): number {
    const atm = this.body.atmosphere;
    if (atm.surfaceDensity <= 0) return 0;
    if (altitude >= atm.topAltitude) return 0;
    const h = Math.max(0, altitude);
    const raw = atm.surfaceDensity * Math.exp(-h / atm.scaleHeight);
    const taper = smoothstep(atm.topAltitude, atm.topAltitude * 0.85, h);
    return raw * taper;
  }

  pressureAt(altitude: number): number {
    const atm = this.body.atmosphere;
    if (atm.surfacePressure <= 0) return 0;
    if (altitude >= atm.topAltitude) return 0;
    const h = Math.max(0, altitude);
    return atm.surfacePressure * Math.exp(-h / atm.scaleHeight) * smoothstep(atm.topAltitude, atm.topAltitude * 0.85, h);
  }

  /**
   * Local air temperature. Falls with altitude at the body's lapse rate down to
   * a floor, then holds constant, mimicking a tropopause.
   */
  temperatureAt(altitude: number, latitudeFactor = 0, solarFactor = 0.5): number {
    const th = this.body.def.thermal;
    const base =
      th.meanSurfaceTemperature -
      th.polarDelta * clamp(latitudeFactor, 0, 1) +
      th.diurnalRange * (clamp(solarFactor, 0, 1) - 0.5);
    const dropped = base - th.lapseRate * Math.max(0, altitude);
    const floor = Math.max(4, base * 0.42);
    return Math.max(floor, dropped);
  }

  speedOfSoundAt(altitude: number, latitudeFactor = 0, solarFactor = 0.5): number {
    const atm = this.body.atmosphere;
    if (atm.molarMass <= 0) return 0;
    const T = this.temperatureAt(altitude, latitudeFactor, solarFactor);
    return Math.sqrt((GAMMA * R_GAS * T) / atm.molarMass);
  }

  /**
   * Wind in the body-fixed frame: a zonal (east-west) base flow that scales
   * with latitude, plus low-frequency turbulence that varies in space and time.
   *
   * Note that this is *on top of* rigid co-rotation. The atmosphere as a whole
   * rotates with the planet; `relativeAirVelocity` handles that part.
   */
  windAt(positionFixed: Vec3, t: number, out = new Vec3()): Vec3 {
    const atm = this.body.atmosphere;
    if (atm.windSpeed <= 0) {
      return out.set(0, 0, 0);
    }
    const r = positionFixed.length();
    if (r < 1e-6) return out.set(0, 0, 0);

    const up = positionFixed.clone().mulScalar(1 / r);
    let east = Vec3.crossVectors(new Vec3(0, 1, 0), up);
    if (east.lengthSq() < 1e-12) east = Vec3.anyPerpendicular(up);
    east.normalize();
    const north = Vec3.crossVectors(up, east).normalize();

    // Zonal flow: strongest in the mid-latitudes, reversed near the equator.
    const sinLat = clamp(up.y, -1, 1);
    const zonal = atm.windSpeed * (1.4 * sinLat * sinLat - 0.4);

    // Turbulence sampled from a slowly drifting noise field.
    const s = 1 / 250000;
    const drift = t * 2e-5;
    const nx = fbm3(positionFixed.x * s + drift, positionFixed.y * s, positionFixed.z * s, { octaves: 3, seed: 91 });
    const ny = fbm3(positionFixed.x * s, positionFixed.y * s + drift, positionFixed.z * s, { octaves: 3, seed: 271 });
    const nz = fbm3(positionFixed.x * s, positionFixed.y * s, positionFixed.z * s + drift, { octaves: 3, seed: 613 });
    const gust = atm.windSpeed * atm.turbulence;

    out.set(
      east.x * zonal + north.x * ny * gust + up.x * nz * gust * 0.3,
      east.y * zonal + north.y * ny * gust + up.y * nz * gust * 0.3,
      east.z * zonal + north.z * ny * gust + up.z * nz * gust * 0.3,
    );
    out.addScaled(east, nx * gust);
    return out;
  }

  sample(altitude: number, positionFixed: Vec3, t: number, latitudeFactor = 0, solarFactor = 0.5): AtmosphereSample {
    return {
      density: this.densityAt(altitude),
      pressure: this.pressureAt(altitude),
      temperature: this.temperatureAt(altitude, latitudeFactor, solarFactor),
      speedOfSound: this.speedOfSoundAt(altitude, latitudeFactor, solarFactor),
      wind: this.windAt(positionFixed, t),
    };
  }

  /**
   * Velocity of the air relative to an inertial observer, at a position given in
   * the body's inertial frame.
   *
   * The atmosphere co-rotates with the body, so a vessel hovering at a fixed
   * inertial point over the equator experiences a headwind equal to the surface
   * rotation speed. This is what makes eastward launches cheaper.
   */
  airVelocityInertial(positionInertial: Vec3, windFixed: Vec3, bodyFixedToInertial: (v: Vec3) => Vec3): Vec3 {
    const corotation = this.body.surfaceVelocityAt(positionInertial);
    return corotation.add(bodyFixedToInertial(windFixed));
  }
}
